// RecordSubmissionDialog.tsx — wireframe S6's "Record submission" dialog
// (submitted on, channel, docket / upload ref, amount claimed → status
// Submitted), shared by the Forms & packet tab and the Documents tab so the
// two entry points can't drift. `POST /api/claims/:id/submissions` already
// enforces that the claim is `discharged`; this only offers the action then.
import { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { useCreateSubmission } from './hooks';
import type { ClaimWorkspace } from './types';

type Channel = 'portal' | 'email' | 'courier' | 'hand';
const CHANNEL_LABEL: Record<Channel, string> = { portal: 'TPA / insurer portal', email: 'Email', courier: 'Courier', hand: 'By hand' };

/** The amount the hospital is claiming: the final approval for a cashless
 * claim (what the insurer agreed to pay), else the latest running bill. */
function defaultAmount(claim: ClaimWorkspace): string {
  if (claim.final_approved != null) return String(Number(claim.final_approved));
  if (claim.running_bill?.net != null) return String(claim.running_bill.net);
  return '';
}

function nowLocal(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

export function RecordSubmissionDialog({ claim, open, onClose }: { claim: ClaimWorkspace; open: boolean; onClose: () => void }) {
  const mutation = useCreateSubmission(claim.id);
  const [submittedAt, setSubmittedAt] = useState(nowLocal);
  const [channel, setChannel] = useState<Channel>('portal');
  const [docketRef, setDocketRef] = useState('');
  const [amount, setAmount] = useState(() => defaultAmount(claim));
  const [error, setError] = useState('');

  const save = async () => {
    setError('');
    if (!(Number(amount) >= 0) || amount === '') { setError('Enter the amount claimed.'); return; }
    try {
      await mutation.mutateAsync({
        submitted_at: new Date(submittedAt).toISOString(), channel,
        docket_ref: docketRef.trim() || undefined, amount_claimed: Number(amount),
      });
      onClose();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Record submission</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label="Submitted on" required>
            <Input type="datetime-local" value={submittedAt} onChange={(e) => setSubmittedAt(e.target.value)} />
          </FormField>
          <FormField label="Channel">
            <NativeSelect value={channel} onChange={(e) => setChannel(e.target.value as Channel)}>
              {(Object.keys(CHANNEL_LABEL) as Channel[]).map((c) => <option key={c} value={c}>{CHANNEL_LABEL[c]}</option>)}
            </NativeSelect>
          </FormField>
          <FormField label="Docket / upload ref">
            <Input value={docketRef} onChange={(e) => setDocketRef(e.target.value)} />
          </FormField>
          <FormField label="Amount claimed ₹" required>
            <Input type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </FormField>
        </div>
        {error && <div className="mt-2 text-[12px] text-danger-ink">{error}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <DialogPrimitive.Close asChild><Button variant="ghost">Cancel</Button></DialogPrimitive.Close>
          <Button loading={mutation.isPending} onClick={save}>Save → status: Submitted</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
